import { useMemo } from "react";
import { useChatMessages } from "@/hooks/use-chat-messages";
import { useChatActions } from "@/hooks/use-chat-actions";
import { useCreateComment, useProjectComments } from "@/hooks/use-project-comments";
import { getDocumentUrl, useProjectDocuments, useUploadDocument } from "@/hooks/use-project-documents";
import { useMarkProjectCommentsRead } from "@/hooks/use-project-comment-reads";
import { useProjectChannelEvents } from "@/hooks/use-project-channel-events";
import { eventsToMessages } from "@/lib/chat-room/system-events";
import { fromChatMessage, projectMessagesFrom, sortMessages, type ChannelMessage } from "@/lib/chat-room/messages";
import type { Channel } from "@/lib/chat-room/channels";

export interface SendChannelMessageArgs {
  body: string;
  file: File | null;
  mentionedUserIds: string[];
}

export interface ChannelMessages {
  messages: ChannelMessage[];
  isLoading: boolean;
  hasMore: boolean;
  isFetchingMore: boolean;
  loadMore: () => void;
  send: (args: SendChannelMessageArgs) => Promise<void>;
  markRead: () => void;
}

/**
 * Meldingene i én kanal i Chat-rommet, uavhengig av kilde (VIB-3022).
 * Rom leser `chat_messages`; prosjektkanaler leser og skriver
 * `project_comments`, så de er samme strøm som Prosjektoppdateringer
 * (VIB-3024), flettet med systemmeldinger fra `project_events` (VIB-3026).
 * `selfId` er den ekte sesjonen.
 */
export function useChannelMessages(channel: Channel | null, selfId: string | null): ChannelMessages {
  const isProject = channel?.kind === "project";
  const roomId = channel && !isProject ? channel.id : null;
  const projectId = channel && isProject ? channel.id : undefined;

  const room = useChatMessages(roomId);
  const { sendMessage, markAsRead } = useChatActions(selfId);
  const comments = useProjectComments(projectId);
  const documents = useProjectDocuments(projectId);
  const events = useProjectChannelEvents(projectId);
  const createComment = useCreateComment();
  const uploadDocument = useUploadDocument();
  const markProjectRead = useMarkProjectCommentsRead(selfId);

  const messages = useMemo(
    () =>
      isProject
        ? sortMessages([
            ...projectMessagesFrom(comments.data ?? [], documents.data ?? [], getDocumentUrl),
            ...eventsToMessages(events.data ?? []),
          ])
        : sortMessages(room.messages.map(fromChatMessage)),
    [isProject, comments.data, documents.data, events.data, room.messages],
  );

  const sendToProject = async ({ body, file, mentionedUserIds }: SendChannelMessageArgs) => {
    if (!projectId || !selfId) return;
    const commentId = await createComment.mutateAsync({
      project_id: projectId,
      author_id: selfId,
      body: body || `📎 ${file?.name ?? "Vedlegg"}`,
      mentioned_user_ids: mentionedUserIds,
      silent: true,
    });
    if (file) await uploadDocument.mutateAsync({ projectId, file, uploadedById: selfId, commentId });
  };

  return {
    messages,
    isLoading: isProject ? comments.isLoading : room.isLoading,
    hasMore: isProject ? false : !!room.hasMore,
    isFetchingMore: isProject ? false : room.isFetchingMore,
    loadMore: () => {
      if (!isProject) void room.loadMore();
    },
    send: async (args) => {
      if (isProject) return sendToProject(args);
      if (!roomId || !selfId) return;
      await sendMessage.mutateAsync({
        roomId,
        senderId: selfId,
        body: args.body,
        file: args.file,
        mentionedUserIds: args.mentionedUserIds,
      });
    },
    markRead: () => {
      if (projectId) markProjectRead.mutate({ projectId });
      else if (roomId) markAsRead.mutate(roomId);
    },
  };
}
